// ── gameStatesStore — batched Unabated gameState map ──────────────
//
// Polls hydra's /api/analytics/game-states for the union of visible
// dashboard gameKeys and stores the latest result. Consumers (the
// value dashboard's GameTable, marketStore.getGames for "final"
// override, etc.) read from this store instead of opening per-game
// SSE connections.
//
// Pattern: ValueDashboard owns the visible-key set + the polling
// effect; everyone else just reads via `useGameStateFor(gameKey)` or
// `useGameStatesStore.getState().getState(gameKey)` for non-React
// consumers.

import { create } from 'zustand';
import type { UnabatedGameState } from '../services/hydraAnalytics';

interface GameStatesStore {
  byGameKey: Map<string, UnabatedGameState | null>;
  /** ms-since-epoch of the last successful fetch. Drives staleness UX. */
  lastFetchMs: number;
  /** Last fetch error (if any). Null when the most recent fetch succeeded. */
  error: string | null;

  setStates: (states: Record<string, UnabatedGameState | null>, serverTs: number) => void;
  setError: (message: string | null) => void;
  /** Returns the latest known state for a gameKey, or null if hydra
   *  hasn't seen it yet (or the poller hasn't fetched it). */
  getState: (gameKey: string) => UnabatedGameState | null;
}

export const useGameStatesStore = create<GameStatesStore>((set, get) => ({
  byGameKey: new Map(),
  lastFetchMs: 0,
  error: null,
  setStates: (states, serverTs) => {
    // Replace the map wholesale — hydra returns the full requested set
    // every poll, so a key dropping out of the response means the
    // caller stopped subscribing to it. Keep a fresh Map identity so
    // Zustand subscribers see the change.
    const next = new Map<string, UnabatedGameState | null>();
    for (const [k, v] of Object.entries(states)) {
      next.set(k, v);
    }
    set({ byGameKey: next, lastFetchMs: serverTs, error: null });
  },
  setError: (message) => set({ error: message }),
  getState: (gameKey) => get().byGameKey.get(gameKey) ?? null,
}));

/**
 * React hook for one game's state. Returns `null` until the poller
 * has fetched it (or until hydra reports the gameKey unknown).
 */
export function useGameStateFor(gameKey: string | null | undefined): UnabatedGameState | null {
  return useGameStatesStore((s) => (gameKey ? s.byGameKey.get(gameKey) ?? null : null));
}
